import React, {useEffect, useMemo, useState} from 'react';
import {Template} from "../../../../../components/template/template.jsx";
import {Card} from "../../../../../components/card/card.jsx";
import Button from "../../../../../components/button/button.jsx";
import {FaCheck} from "react-icons/fa";
import {navigateHook} from "../../../../../hooks/use-navigate.js";
import projectUtils from "../../../../../utils/project-utils.js";
import citizenScheduledVisitService from "../../../../../services/web/citizen-scheduled-visit-service.js";
import {conflictInfo, isRetroactive, matchingAddresses, translateScheduledVisitError} from "../utils/schedule-utils.js";
import ScheduleDefinitionStep from "./components/schedule-definition-step.jsx";
import ScheduleCitizenSelectionStep from "./components/schedule-citizen-selection-step.jsx";
import ScheduleConfirmationStep from "./components/schedule-confirmation-step.jsx";
import {FaTriangleExclamation} from "react-icons/fa6";
import Alert from "../../../../../components/alert/alert.jsx";

const STEPS = [
    {number: 1, label: "Tipo e horário"},
    {number: 2, label: "Protegidos"},
    {number: 3, label: "Agendamento"},
];

const initialSchedule = {
    type: "",
    date: "",
    startTime: "",
    endTime: "",
};

const CitizenScheduledVisitForm = () => {
    const navigate = navigateHook();

    const [step, setStep] = useState(1);
    const [schedule, setSchedule] = useState(initialSchedule);
    const [citizens, setCitizens] = useState([]);
    const [addressSelection, setAddressSelection] = useState({});
    const [timeConfirmed, setTimeConfirmed] = useState({});
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        setCitizens([]);
    }, [schedule.type]);

    useEffect(() => {
        setTimeConfirmed({});
    }, [schedule.type, schedule.date, schedule.startTime, schedule.endTime]);

    useEffect(() => {
        setAddressSelection(prev => {
            const next = {};

            citizens.forEach(citizen => {
                const matches = matchingAddresses(citizen, schedule.type);
                const previous = (prev[citizen.id] ?? []).filter(id => matches.some(a => a.id === id));

                next[citizen.id] = previous.length || matches.length !== 1 ? previous : [matches[0].id];
            });

            return next;
        });

        setTimeConfirmed(prev => {
            const next = {};

            citizens.forEach(citizen => {
                if (prev[citizen.id]) next[citizen.id] = prev[citizen.id];
            });

            return next;
        });
    }, [citizens, schedule.type]);

    const removeCitizen = (id) => setCitizens(prev => prev.filter(c => c.id !== id));

    const resolveConflict = (citizenId, addressIds, confirmed) => {
        setAddressSelection(prev => ({...prev, [citizenId]: addressIds}));
        setTimeConfirmed(prev => ({...prev, [citizenId]: confirmed}));
    };

    const validateDefinition = () => {
        if (!schedule.type) {
            projectUtils.swalErrorMessage('', "Selecione o tipo de agendamento.", "Ok");
            return false;
        }

        if (!schedule.date) {
            projectUtils.swalErrorMessage('', "O campo \"Data\" é obrigatório.", "Ok");
            return false;
        }

        if (!schedule.startTime) {
            projectUtils.swalErrorMessage('', "O campo \"Horário inicial\" é obrigatório.", "Ok");
            return false;
        }

        if (!schedule.endTime) {
            projectUtils.swalErrorMessage('', "O campo \"Horário final\" é obrigatório.", "Ok");
            return false;
        }

        if (schedule.startTime >= schedule.endTime) {
            projectUtils.swalErrorMessage('', "O horário final deve ser maior que o inicial.", "Ok");
            return false;
        }

        if (isRetroactive(schedule)) {
            projectUtils.swalErrorMessage('', "O Data/hora do agendamento deve ser maior que o atual.", "Ok");
            return false;
        }

        return true;
    };

    const validateProtections = () => {
        if (!citizens.length) {
            projectUtils.swalErrorMessage('', "Selecione ao menos um protegido.", "Ok");
            return false;
        }

        return true;
    };

    const next = () => {
        if (step === 1 && !validateDefinition()) return;
        if (step === 2 && !validateProtections()) return;

        setStep(prev => Math.min(prev + 1, STEPS.length));
    };

    const back = () => {
        if (step === 1) {
            navigate("/sos/citizen-scheduled-visit");
            return;
        }

        setStep(prev => Math.max(prev - 1, 1));
    };

    const save = () => {
        if (!citizens.length) {
            projectUtils.swalErrorMessage('', "Selecione ao menos um protegido.", "Ok");
            return;
        }

        if (isRetroactive(schedule)) {
            projectUtils.swalErrorMessage('', "O Data/hora do agendamento deve ser maior que o atual.", "Ok");
            return;
        }

        const pending = citizens.find(citizen => {
            const {hasConflict} = conflictInfo(citizen, schedule.type, schedule, addressSelection[citizen.id] ?? [], timeConfirmed[citizen.id]);
            return hasConflict || !(addressSelection[citizen.id]?.length);
        });

        if (pending) {
            projectUtils.swalErrorMessage('', "Resolva os conflitos de endereço/horário antes de salvar.", "Ok");
            return;
        }

        const scheduleWindowStartDt = `${schedule.date} ${schedule.startTime}`;
        const scheduleWindowEndDt = `${schedule.date} ${schedule.endTime}`;

        const items = citizens.flatMap(citizen =>
            (addressSelection[citizen.id] ?? []).map(addressId => {
                const address = (citizen.addresses ?? []).find(a => a.id === addressId) ?? {};

                return {
                    citizenId: citizen.id,
                    type: schedule.type,
                    scheduleWindowStartDt,
                    scheduleWindowEndDt,
                    latitude: address.latitude,
                    longitude: address.longitude,
                    addressType: address.addressType,
                    address: address.address,
                    number: address.number,
                    complement: address.complement,
                    neighborhood: address.neighborhood,
                    city: address.city,
                    zipCode: address.zipCode,
                    sector: address.sector,
                };
            })
        );

        setIsSaving(true);

        citizenScheduledVisitService.create(items)
            .then(() => {
                projectUtils.success(true);
                navigate("/sos/citizen-scheduled-visit");
            })
            .catch((error) => {
                console.error(error);

                let message = "Não foi possível salvar o agendamento.";
                if (error.response?.status !== 500) {
                    message = translateScheduledVisitError(error.response?.data?.message, message);
                }

                projectUtils.swalErrorMessage('', message, 'Ok')
            })
            .finally(() => setIsSaving(false));
    };

    const hasPending = useMemo(() => citizens.some(citizen => {
        const {hasConflict} = conflictInfo(citizen, schedule.type, schedule, addressSelection[citizen.id] ?? [], timeConfirmed[citizen.id]);
        return hasConflict || !(addressSelection[citizen.id]?.length);
    }), [citizens, schedule, addressSelection, timeConfirmed]);

    return (
        <Template>
            <Template.Header>
                <Template.Title>Novo agendamento</Template.Title>
            </Template.Header>

            <Template.Body className={"space-y-2 flex flex-col"}>
                <Card>
                    <Card.Body className={"flex items-center justify-center"}>
                        {
                            STEPS.map(({number, label}) => {
                                const completed = step > number;
                                const active = step === number;
                                const reachable = number < step;

                                return (
                                    <React.Fragment key={number}>
                                        <button
                                            type="button"
                                            onClick={() => reachable && setStep(number)}
                                            className={["flex items-center gap-3", reachable ? "cursor-pointer" : "cursor-default"].join(" ")}
                                        >
                                            <span className={[
                                                "flex items-center justify-center w-9 h-9 rounded-full text-sm font-bold border-2 transition-colors",
                                                completed
                                                    ? "bg-[#063A6C] border-[#063A6C] text-white"
                                                    : active
                                                        ? "bg-[#063A6C] border-[#063A6C] text-white"
                                                        : "bg-white border-gray-300 text-gray-400"
                                            ].join(" ")}>
                                                {completed ? <FaCheck size={13}/> : number}
                                            </span>

                                            <div className={"text-left leading-tight"}>
                                                <small className={"font-bold opacity-90"}>ETAPA {number}</small>
                                                <p className={["text-sm font-semibold", active ? "text-gray-800" : completed ? "text-gray-600" : "text-gray-400"].join(" ")}>
                                                    {label}
                                                </p>
                                            </div>
                                        </button>

                                        {
                                            number < STEPS.length &&
                                            <div className={"flex-1 max-w-30 h-0.5 mx-4 rounded bg-gray-200 overflow-hidden"}>
                                                <div className={["h-full bg-[#063A6C] transition-all duration-300", completed ? "w-full" : "w-0"].join(" ")}/>
                                            </div>
                                        }
                                    </React.Fragment>
                                )
                            })
                        }
                    </Card.Body>
                </Card>

                {
                    step === 1 &&
                    <>
                        <ScheduleDefinitionStep data={schedule} setData={setSchedule}/>
                    </>
                }

                {
                    step === 2 &&
                    <ScheduleCitizenSelectionStep type={schedule.type} selected={citizens} setSelected={setCitizens}/>
                }

                {
                    step === 3 &&
                    <ScheduleConfirmationStep
                        schedule={schedule}
                        citizens={citizens}
                        addressSelection={addressSelection}
                        timeConfirmed={timeConfirmed}
                        onResolve={resolveConflict}
                        onRemove={removeCitizen}
                    />
                }

                <Card className={"mt-auto"}>
                    <Card.Body className={"flex justify-between w-full gap-2"}>
                        <Button target={"default"} onClick={back}>
                            {step === 1 ? "Cancelar" : "Voltar"}
                        </Button>

                        {
                            step < STEPS.length &&
                            <Button onClick={next}>
                                Próximo
                            </Button>
                        }

                        {
                            step === STEPS.length &&
                            <section className={"flex gap-2"}>
                                {
                                    hasPending &&
                                    <Alert className={"flex items-center gap-3 py-0! px-5! h-8!"} target={"warning"}>
                                        <FaTriangleExclamation /> Resolva os conflitos para confirmar os agendamentos.
                                    </Alert>
                                }

                                <Button onClick={save} loading={isSaving} disabled={!citizens.length || hasPending} actionFeedbackType="save">
                                    Salvar
                                </Button>
                            </section>
                        }
                    </Card.Body>
                </Card>
            </Template.Body>
        </Template>
    );
};

export default CitizenScheduledVisitForm;